import { FC, useCallback, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { Button as AmbrosiaButton, Modal } from '@payaca/ambrosia';
import ContentPanel from '@payaca/components/contentPanel/ContentPanel';
import EditJobSection from '../editJob/EditJobSection';
import JobLineItemsControl from '../jobLineItemsControl/JobLineItemsControl';
import SelectItemGroupModal from '../selectItemGroupModal/SelectItemGroupModal';
import SelectItemModal from '../selectItemModal/SelectItemModal';

import { getJobContent } from '@/utils/stateAccessors';

import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';

import { JobContent, JobLineItemBase } from '@payaca/types/jobContentTypes';

import { useSelector } from '@/api/state';

import './InvoiceLineItemsControl.sass';

type Props = {
  jobIsInvoice: boolean;
  jobContentId: number;
};

const InvoiceLineItemsControl: FC<Props> = ({
  jobIsInvoice,
  jobContentId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [
    showLineItemSearchModalForGroupId,
    setShowLineItemSearchModalForGroupId,
  ] = useState<null | number>();
  const [isCreatingJobLineItem, setIsCreatingJobLineItem] = useState(false);
  const [isAddingExistingJobLineItem, setIsAddingExistingJobLineItem] =
    useState(false);

  const jobContent: JobContent | undefined = useSelector((state) =>
    getJobContent(state, jobContentId)
  );

  const isAddingLineItemGroupToJobContent = useSelector((state) => {
    return state.jobContent.isAddingLineItemGroupToJobContent;
  });

  const lineItemGroups = useSelector(
    (state) => state.lineItemGroups.lineItemGroups
  );

  const [showLineItemGroupModal, setShowLineItemGroupModal] =
    useState<boolean>(false);
  const [showItemGroupConfirmation, setShowItemGroupConfirmation] =
    useState<boolean>(false);

  const [lineItemGroupToAdd, setLineItemGroupToAdd] = useState<number | null>(
    null
  );

  const hasAnyLineItemGroups = useMemo(() => {
    return !!Object.values(lineItemGroups)?.length;
  }, [lineItemGroups]);

  const addLineItemGroupToJobContent = useCallback(
    (lineItemGroupId: number) => {
      dispatch(
        jobContentActions.requestAddLineItemGroupToJobContent(
          jobContentId,
          lineItemGroupId,
          () => {
            dispatch(
              jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
                jobContentId
              )
            );
          }
        )
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [jobContentId]
  );

  const createJobLineItem = useCallback(
    (
      jobLineItemGroupId: number,
      lineItemId: number | null,
      initialItemData?: Partial<JobLineItemBase>,
      callback?: (jobLineItemId: number) => void
    ) => {
      if (lineItemId) {
        setIsAddingExistingJobLineItem(true);
      } else {
        setIsCreatingJobLineItem(true);
      }

      dispatch(
        jobContentActions.requestCreateJobLineItem(
          {
            ...initialItemData,
            jobContentId,
            jobLineItemGroupId,
            lineItemId,
          },
          (jobLineItemId: number) => {
            setIsCreatingJobLineItem(false);
            setIsAddingExistingJobLineItem(false);
            dispatch(
              jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
                jobContentId
              )
            );
            callback?.(jobLineItemId);
          }
        )
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [jobContentId]
  );

  const createJobLineItemAndGoToEditJobLineItemPage = (
    jobLineItemGroupdId: number,
    lineItemId: number | null,
    initialItemData?: Partial<JobLineItemBase>
  ) => {
    if (jobLineItemGroupdId) {
      // create job line item
      createJobLineItem(
        jobLineItemGroupdId,
        lineItemId,
        initialItemData,
        (jobLineItemId: number) => {
          if (!lineItemId) {
            // open edit item modal if new item not from li
            goToEditJobLineItemPage(jobLineItemId);
          }
        }
      );
    }
  };

  const goToEditJobLineItemPage = (jobLineItemId: number) => {
    history.push(`${location.pathname}/items/${jobLineItemId}`);
  };

  return (
    <EditJobSection
      title={jobIsInvoice ? 'Invoice lines' : 'Items'}
      className="invoice-line-items-control"
      description={
        <p>
          Add an existing item or{' '}
          <AmbrosiaButton
            variant="link"
            onClick={() => {
              const jobLineItemGroupId = jobContent?.jobLineItemGroupIds[0];
              if (jobLineItemGroupId && !isCreatingJobLineItem) {
                createJobLineItemAndGoToEditJobLineItemPage(
                  jobLineItemGroupId,
                  null
                );
              }
            }}
            disabled={
              !jobContent?.jobLineItemGroupIds[0] || isCreatingJobLineItem
            }
          >
            create a new Item
          </AmbrosiaButton>
        </p>
      }
    >
      <ContentPanel hasBoxShadow={false}>
        <>
          {jobContent?.jobLineItemGroupIds?.map((jobLineItemGroupId, index) => (
            <JobLineItemsControl
              jobIsInvoice={jobIsInvoice}
              jobLineItemGroupId={jobLineItemGroupId}
              key={`job-line-items-control-${index}`}
              handleOpenCreateJobLineItemModal={goToEditJobLineItemPage}
              handleOpenAdvancedSearchLineItemModal={() =>
                setShowLineItemSearchModalForGroupId(jobLineItemGroupId)
              }
              handleCreateJobLineItemAndOpenCreateJobLineItemModal={(
                lineItemId: number | null,
                initialItemData?: Partial<JobLineItemBase>
              ) =>
                createJobLineItemAndGoToEditJobLineItemPage(
                  jobLineItemGroupId,
                  lineItemId,
                  initialItemData
                )
              }
              disabledItemIds={jobContent?.lineItemIds || []}
            />
          ))}
        </>
      </ContentPanel>
      {hasAnyLineItemGroups && (
        <div className="create-or-add-existing-container line-item-group">
          <AmbrosiaButton
            onClick={() => setShowLineItemGroupModal(true)}
            isProcessing={isAddingLineItemGroupToJobContent}
            replaceContentWhenProcessing
            variant="link"
          >
            Search existing groups
          </AmbrosiaButton>
        </div>
      )}
      <SelectItemGroupModal
        isOpen={showLineItemGroupModal}
        onClose={() => setShowLineItemGroupModal(false)}
        onSelectItemGroup={(lineItemGroup: any) => {
          if (lineItemGroupToAdd) {
            // don't allow selecting a group if already adding one
            return;
          }

          const hasOptionalMultipleChoiceItems =
            lineItemGroup.lineItemRelations.filter(
              (lir: any) => lir.isOptional || lir.isSelected
            ).length;

          if (!hasOptionalMultipleChoiceItems) {
            addLineItemGroupToJobContent(lineItemGroup.id);
          } else {
            setLineItemGroupToAdd(lineItemGroup.id);
            setShowItemGroupConfirmation(true);
          }

          setShowLineItemGroupModal(false);
        }}
      />
      {/* search item modal */}
      <SelectItemModal
        isOpen={!!showLineItemSearchModalForGroupId}
        onClose={() => setShowLineItemSearchModalForGroupId(null)}
        onSelectItem={(lineItemId: number) => {
          if (
            !isCreatingJobLineItem &&
            !isAddingExistingJobLineItem &&
            showLineItemSearchModalForGroupId
          ) {
            // close select item modal
            setShowLineItemSearchModalForGroupId(null);
            // create job line item and open edit modal
            createJobLineItemAndGoToEditJobLineItemPage(
              showLineItemSearchModalForGroupId,
              lineItemId
            );
          }
        }}
        disabledItemIds={jobContent?.lineItemIds || []}
      />
      <Modal
        isOpen={showItemGroupConfirmation}
        title="Item group contains optional or multiple choice items"
        onClose={() => {
          setLineItemGroupToAdd(null);
          setShowItemGroupConfirmation(false);
        }}
        size="sm"
      >
        <Modal.Body>
          Item group contains items which are set as optional or multiple
          choice, these items will be made required if added to the invoice.
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <AmbrosiaButton
              variant="secondary"
              onClick={() => {
                setLineItemGroupToAdd(null);
                setShowItemGroupConfirmation(false);
              }}
            >
              Cancel
            </AmbrosiaButton>
            <AmbrosiaButton
              onClick={() => {
                addLineItemGroupToJobContent(lineItemGroupToAdd as number);
                setLineItemGroupToAdd(null);
                setShowItemGroupConfirmation(false);
              }}
            >
              Add item group
            </AmbrosiaButton>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </EditJobSection>
  );
};
export default InvoiceLineItemsControl;
